import { ModelEntityType } from '../../../model/input/types';

export const entityTypesForAccessDetails = [
  ModelEntityType.COMPANY,
  ModelEntityType.GROUP,
  ModelEntityType.PORTFOLIO,
] as const;
export type EntityTypeForAccessDetails = (typeof entityTypesForAccessDetails)[number];

export function assertIsEntityTypeForAccessDetails(type: ModelEntityType): asserts type is EntityTypeForAccessDetails {
  if (entityTypesForAccessDetails.includes(type as EntityTypeForAccessDetails)) return;
  throw Error(`Expected [${entityTypesForAccessDetails.join(', ')}] to include ${type}.`);
}

type AccessDetailsDetailsByEntityType = {
  [ModelEntityType.COMPANY]: CompanyAccessDetails;
  [ModelEntityType.GROUP]: CompanyGroupAccessDetails;
  [ModelEntityType.PORTFOLIO]: PortfolioAccessDetails;
};

export type AccessDetails<T extends EntityTypeForAccessDetails = EntityTypeForAccessDetails> =
  AccessDetailsDetailsByEntityType[T];

export type CompanyAccessDetails = {
  [DataSetId.netImpact]: SingleDataSetAccessDetails;
  [DataSetId.sdgAlignment]: SingleDataSetAccessDetails;
  [DataSetId.euTaxonomy]: SingleDataSetAccessDetails;
  [DataSetId.euSfdr]: SingleDataSetAccessDetails;
  [DataSetId.csrd]: CsrdAccessDetails;
  [DataSetId.financialEffects]: RecomputableDataSetAccessDetails;
  [DataSetId.climateRisk]: RecomputableDataSetAccessDetails;
  [DataSetId.insights]: SingleDataSetAccessDetails;
  [DataSetId.targets]: SingleDataSetAccessDetails;
  // PLT-4790: PLG Enterprise "Draft mode". `true` when the profile-level
  // `draftMode` flag is on AND the viewer is NOT an Upright-team user.
  // Customer users in this state see input pages locked, recompute hidden,
  // and DMA result pages paywalled with the "results being worked on" copy.
  // Team users always see this as `false` and are unaffected by draft mode.
  isInDraftMode: boolean;
  // PLT-5806: PLG "Hide all results". `true` when the profile-level
  // `hideAllResults` flag is on AND the viewer is NOT an Upright-team user.
  // Customer users in this state see every PLG dashboard results lens (DMA,
  // Financial effects, Climate) blurred/paywalled as a profile with no results
  // yet — but, unlike draft mode, the input pages stay editable. Team users
  // always see this as `false`.
  isHidingAllResults: boolean;
};

export type CompanyGroupAccessDetails = { [dataSetId in CompanyGroupDataSetId]: SingleDataSetAccessDetails };
export type PortfolioAccessDetails = { [dataSetId in PortfolioDataSetId]: SingleDataSetAccessDetails };

export type InaccessibleDataAccessDetails = {
  isAccessibleToUser: false;
  isDataAvailable: undefined;
};

export type AccessibleDataAccessDetails = {
  isAccessibleToUser: true;
  isDataAvailable: boolean;
};

export type SingleDataSetAccessDetails = InaccessibleDataAccessDetails | AccessibleDataAccessDetails;

// PLT-5834: a dataset that additionally exposes whether the user may trigger a (paid) recompute
// of it. `canCalculate` is gated on the dataset's CALCULATE_* privilege, independently of the
// dataset's own READ_* right (a user may hold recompute rights without dataset read, or vice
// versa). Entity read access is still a precondition — a user with no access to the entity at all
// gets `canCalculate: false`. Consumed by the
// input-tool recompute buttons — the site-locations "Update assessment" (publish) button and the
// financial-statements "Recalculate financial effects" button — so they disable for users who
// lack the recompute right (the backend endpoints enforce the same privilege as defence in depth).
export type RecomputableDataSetAccessDetails = SingleDataSetAccessDetails & { canCalculate: boolean };

// OH 5.3.25: The user might have access to the company but not to the CSRD result of it. We still want
// to show a teaser of the data and, hence, return information of whether such data is available.
// If the user has access to the CSRD result, we also return information of whether the result has financial
// effects calculated so that the UI knows which components its going to render before the data arrives.
export type InaccessibleResultDetailsCsrd = {
  hasAccessToCsrdResult: false;
  isFinancialEffectsDataAvailable: undefined;
};

export type AccessibleResultDetailsCsrd = {
  hasAccessToCsrdResult: true;
  isFinancialEffectsDataAvailable: boolean;
  canReadTailoring: boolean;
  canEditTailoring: boolean;
};

type CsrdSpecificAccessDetails = InaccessibleResultDetailsCsrd | AccessibleResultDetailsCsrd;

export type CsrdAccessDetails =
  | InaccessibleDataAccessDetails
  | (AccessibleDataAccessDetails & CsrdSpecificAccessDetails);

export enum DataSetId {
  netImpact = 'netImpact',
  sdgAlignment = 'sdgAlignment',
  euTaxonomy = 'euTaxonomy',
  euSfdr = 'euSfdr',
  csrd = 'csrd',
  financialEffects = 'financialEffects',
  climateRisk = 'climateRisk',
  insights = 'insights',
  targets = 'targets',
}

export type CompanyDataSetId =
  | DataSetId.netImpact
  | DataSetId.sdgAlignment
  | DataSetId.euTaxonomy
  | DataSetId.euSfdr
  | DataSetId.insights
  | DataSetId.targets
  | DataSetId.csrd
  | DataSetId.financialEffects
  | DataSetId.climateRisk;

export type CompanyGroupDataSetId =
  | DataSetId.netImpact
  | DataSetId.sdgAlignment
  | DataSetId.euTaxonomy
  | DataSetId.euSfdr
  | DataSetId.csrd;

export type PortfolioDataSetId = DataSetId.netImpact | DataSetId.sdgAlignment | DataSetId.euTaxonomy | DataSetId.euSfdr;
